import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { UserProfile } from '../types/user';
import BasePageLayout from '../components/BasePageLayout';
import ProjectCreationEligibilityCard from '../components/projects/ProjectCreationEligibilityCard';
import LinkedInAuthManager from '../components/auth/LinkedInAuthManager';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { LinkedInAuthService, LinkedInAuthStatus } from '../services/linkedinAuthService';
import { ProjectCreationSettingsService, ProjectCreationEligibility, ProjectCreationSettings } from '../services/projectCreationSettingsService';
import '../styles/pages/ProfilePage.css';
import '../styles/layout/wide-screen-layouts.css';
import '../styles/shared/dashboard.css';
import PageLoader from '../components/common/PageLoader';

const ProfilePage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [inputReferralCode, setInputReferralCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const [referralStats, setReferralStats] = useState({
    totalReferred: 0,
    pointsEarned: 0,
    currentStreak: 0,
    milestoneProgress: 25
  });
  const [linkedInAuthStatus, setLinkedInAuthStatus] = useState<LinkedInAuthStatus | null>(null);
  const [projectEligibility, setProjectEligibility] = useState<ProjectCreationEligibility | null>(null);
  const [projectSettings, setProjectSettings] = useState<ProjectCreationSettings | null>(null);

  const { user } = useAuth();
  const isOwnProfile = user && (userId === 'me' || userId === user.id);

  // Fetch user profile
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);
        setAvatarLoadFailed(false);

        const profileId = userId === 'me' ? user?.id : userId;

        if (!profileId) {
          throw new Error('User not found');
        }

        // Fetch all data in parallel to improve performance
        const [userData, linkedInStatus, eligibility, settings] = await Promise.all([
          // Fetch user profile
          supabase
            .from('users')
            .select('id, username, display_name, avatar_url, role, points, completed_tasks, bio, referral_code, referred_by')
            .eq('id', profileId)
            .single()
            .then(({ data, error }) => {
              if (error) throw error;
              if (!data) throw new Error('User not found');
              return data;
            }),

          // Fetch LinkedIn auth status (only for own profile)
          isOwnProfile
            ? LinkedInAuthService.getLinkedInAuthStatus(profileId)
            : Promise.resolve(null),

          // Fetch project creation eligibility (only for own profile)
          isOwnProfile
            ? ProjectCreationSettingsService.checkEligibility(profileId)
            : Promise.resolve(null),

          // Fetch project creation settings (only for own profile)
          isOwnProfile
            ? ProjectCreationSettingsService.getSettings()
            : Promise.resolve(null)
        ]);

        setProfile(userData as UserProfile);
        setDisplayName(userData.display_name || '');
        setBio(userData.bio || '');
        setReferralCode(userData.referral_code || '');
        setLinkedInAuthStatus(linkedInStatus);
        setProjectEligibility(eligibility);
        setProjectSettings(settings);

        // Check if user can still input referral code (hasn't been referred yet)
        setShowReferralInput(Boolean(isOwnProfile && userData.referred_by === null));
      } catch (err) {
        console.error('Error fetching profile data:', err);
        setError('Failed to load profile data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userId, user]);

  // Handle profile update
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !isOwnProfile) return;

    try {
      setLoading(true);

      const { error } = await supabase
        .from('users')
        .update({
          display_name: displayName,
          bio
        })
        .eq('id', user.id);

      if (error) throw error;

      // Update local profile state
      if (profile) {
        setProfile({
          ...profile,
          display_name: displayName,
          bio
        });
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle referral code submission
  const handleReferralSubmit = async () => {
    if (!user || !inputReferralCode.trim()) return;

    try {
      setLoading(true);
      setReferralMessage('');

      const { data, error } = await supabase.rpc('handle_referral_signup', {
        p_user_id: user.id,
        p_referral_code: inputReferralCode.trim().toUpperCase()
      });

      if (error) throw error;

      if (data) {
        setReferralMessage(t('profile.referralSuccess'));
        setShowReferralInput(false);
        // Refresh profile to hide the input
        window.location.reload();
      } else {
        setReferralMessage(t('profile.referralInvalid'));
      }
    } catch (err: any) {
      console.error('Error applying referral code:', err);
      setReferralMessage(t('profile.referralFailed') + ' ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Copy referral code to clipboard
  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setReferralMessage(t('profile.referralCodeCopied'));
      setTimeout(() => setReferralMessage(''), 3000);
    }
  };

  // Render loading state
  if (loading && !profile) {
    return (
      <BasePageLayout>
        <PageLoader fullPage text={t('profile.loading')} />
      </BasePageLayout>
    );
  }

  // Render error state
  if (error || !profile) {
    return (
      <BasePageLayout>
        <div className="alert alert-danger mt-4" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || t('profile.userNotFound')}
        </div>
        <Link to="/" className="btn btn-primary mt-3">
          {t('profile.goToHome')}
        </Link>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="profile-container">
        {/* Header Section - Similar to Points History Page */}
        <section className="profile-header-section">
          <div className="profile-header-content">
            <div className="profile-hero-centered">
              <div className="profile-icon-wrapper">
                <div className="profile-icon-large">
                  {profile.avatar_url && !avatarLoadFailed ? (
                    <img
                      src={profile.avatar_url}
                      alt={`${profile.display_name}'s avatar`}
                      onError={() => setAvatarLoadFailed(true)}
                      className="profile-avatar-image"
                    />
                  ) : (
                    <span className="profile-icon-placeholder">
                      {(profile.display_name || profile.username).charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>

              <div className="profile-info">
                <div className="profile-info-box">
                  <div className="profile-title-group">
                    <h1 className="profile-title-main">{profile.display_name || profile.username}</h1>
                    <div className="profile-badge">
                      <i className="bi bi-person-badge"></i>
                      {profile.role === 'entrepreneur' ? t('profile.entrepreneur') : t('profile.member')}
                    </div>
                  </div>
                  <p className="profile-description">
                    {profile.bio || t('profile.buildingFuture')}
                  </p>

                  <div className="profile-meta">
                    <div className="meta-item">
                      <i className="bi bi-trophy"></i>
                      <span>{t('profile.pointsCount', { count: (profile.points || 0).toLocaleString() })}</span>
                    </div>
                    <div className="meta-item">
                      <i className="bi bi-check-circle"></i>
                      <span>{t('profile.tasksCount', { count: (profile.completed_tasks || 0).toLocaleString() })}</span>
                    </div>
                    <div className="meta-item">
                      <i className="bi bi-people"></i>
                      <span>{t('profile.referralsCount', { count: referralStats.totalReferred })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Floating Edit Button */}
        {isOwnProfile && (
          <button
            className="floating-edit-btn"
            onClick={() => setIsEditing(!isEditing)}
            title={isEditing ? t('profile.cancelEdit') : t('profile.editProfile')}
          >
            <i className={`bi ${isEditing ? 'bi-x-lg' : 'bi-pencil'}`}></i>
          </button>
        )}

        {/* Main Content Container with Points History Page-style Background */}
        <section className="profile-wide-container">
          <div className="profile-main-content">
            <div className="profile-wide-grid">
              {/* Primary Info Panel - Left */}
              <div className="profile-panel-primary">
                {/* Recent Activity Panel */}
                {!isEditing && (
                  <div className="activity-panel">
                    <h3 className="section-title">
                      <i className="bi bi-activity"></i>
                      {t('profile.recentActivity')}
                    </h3>

                    <div className="activity-timeline-compact">
                      <div className="activity-item">
                        <div className="activity-icon login">
                          <i className="bi bi-box-arrow-in-right"></i>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{t('profile.dailyLogin')}</div>
                          <div className="activity-desc">{t('profile.earnedBonusPoints', { points: 10 })}</div>
                          <div className="activity-time">{t('profile.hoursAgo', { hours: 2 })}</div>
                        </div>
                      </div>

                      <div className="activity-item">
                        <div className="activity-icon achievement">
                          <i className="bi bi-award"></i>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{t('profile.achievementUnlocked')}</div>
                          <div className="activity-desc">{t('profile.firstTaskCompleted')}</div>
                          <div className="activity-time">{t('profile.dayAgo', { days: 1 })}</div>
                        </div>
                      </div>

                      <div className="activity-item">
                        <div className="activity-icon task">
                          <i className="bi bi-check-circle"></i>
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{t('profile.taskCompleted')}</div>
                          <div className="activity-desc">{t('profile.socialMediaEngagement')}</div>
                          <div className="activity-time">{t('profile.daysAgo', { days: 2 })}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Point History Panel */}
                {!isEditing && (
                  <div className="points-history-panel">
                    <h3 className="section-title">
                      <i className="bi bi-coin"></i>
                      {t('profile.pointsHistory')}
                    </h3>

                    {/* Emphasized Available Points */}
                    <div className="available-points-display">
                      <div className="available-points-number">{(profile.points || 0).toLocaleString()}</div>
                      <div className="available-points-label">{t('profile.availablePoints')}</div>
                    </div>

                    {/* Recent Transactions - Limited to 3 */}
                    <div className="transactions-list">
                      <div className="transaction-item">
                        <div className="transaction-icon points">
                          <i className="bi bi-plus-circle"></i>
                        </div>
                        <div className="transaction-details">
                          <div className="transaction-title">{t('profile.bonusPointsAmount', { points: 10 })}</div>
                          <div className="transaction-desc">{t('profile.dailyLogin')}</div>
                          <div className="transaction-time">Jul 11, 2025</div>
                        </div>
                      </div>

                      <div className="transaction-item">
                        <div className="transaction-icon task">
                          <i className="bi bi-check-circle"></i>
                        </div>
                        <div className="transaction-details">
                          <div className="transaction-title">{t('profile.taskCompletedAmount', { points: 25 })}</div>
                          <div className="transaction-desc">{t('profile.socialMediaEngagement')}</div>
                          <div className="transaction-time">Jul 10, 2025</div>
                        </div>
                      </div>

                      <div className="transaction-item">
                        <div className="transaction-icon referral">
                          <i className="bi bi-people-fill"></i>
                        </div>
                        <div className="transaction-details">
                          <div className="transaction-title">{t('profile.referralBonusAmount', { points: 50 })}</div>
                          <div className="transaction-desc">{t('profile.friendJoined')}</div>
                          <div className="transaction-time">Jul 9, 2025</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Referral Program Panel - First */}
              {isOwnProfile && (
                <div className="profile-panel-referral">
                  {/* Profile Information Card */}
                  <div className="profile-info-card">
                    <h3 className="section-title">
                      <i className="bi bi-person-circle"></i>
                      {t('profile.profileInformation')}
                    </h3>

                    {/* Profile Stats Row */}
                    <div className="profile-stats-row">
                      <div className="stat-card-compact">
                        <div className="stat-icon blue">
                          <i className="bi bi-trophy"></i>
                        </div>
                        <div className="stat-content">
                          <div className="stat-number">{(profile.points || 0).toLocaleString()}</div>
                          <div className="stat-label">{t('profile.pointsLabel').toUpperCase()}</div>
                        </div>
                      </div>

                      <div className="stat-card-compact">
                        <div className="stat-icon green">
                          <i className="bi bi-check-circle"></i>
                        </div>
                        <div className="stat-content">
                          <div className="stat-number">{(profile.completed_tasks || 0).toLocaleString()}</div>
                          <div className="stat-label">{t('profile.tasksLabel').toUpperCase()}</div>
                        </div>
                      </div>

                      <div className="stat-card-compact">
                        <div className="stat-icon purple">
                          <i className="bi bi-people"></i>
                        </div>
                        <div className="stat-content">
                          <div className="stat-number">{referralStats.totalReferred}</div>
                          <div className="stat-label">{t('profile.referralsLabel').toUpperCase()}</div>
                        </div>
                      </div>
                    </div>

                    {/* Profile Edit Form */}
                    {isEditing && (
                      <div className="profile-edit-section">
                        <form className="profile-edit-form" onSubmit={handleUpdateProfile}>
                          <div className="form-group">
                            <label htmlFor="display_name" className="form-label">{t('profile.displayName')}</label>
                            <input
                              type="text"
                              id="display_name"
                              className="form-control"
                              value={displayName}
                              onChange={(e) => setDisplayName(e.target.value)}
                              disabled={loading}
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="bio" className="form-label">{t('profile.bio')}</label>
                            <textarea
                              id="bio"
                              className="form-control"
                              rows={3}
                              value={bio}
                              onChange={(e) => setBio(e.target.value)}
                              disabled={loading}
                              placeholder={t('profile.bioPlaceholder')}
                            />
                          </div>

                          <div className="form-actions">
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={loading}
                            >
                              {loading ? t('profile.saving') : t('profile.saveChanges')}
                            </button>

                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setIsEditing(false)}
                              disabled={loading}
                            >
                              {t('profile.cancel')}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>

                  {/* Referral Program Card */}
                  <div className="referral-program-card">
                    <h3 className="section-title">
                      <i className="bi bi-people-fill"></i>
                      {t('profile.referralProgram')}
                    </h3>

                    {/* Referral Stats Overview */}
                    <div className="referral-stats-overview">
                      <div className="referral-stat">
                        <div className="referral-stat-number">{referralStats.totalReferred}</div>
                        <div className="referral-stat-label">{t('profile.totalReferred')}</div>
                      </div>
                      <div className="referral-stat">
                        <div className="referral-stat-number">{referralStats.pointsEarned}</div>
                        <div className="referral-stat-label">{t('profile.pointsEarned')}</div>
                      </div>
                      <div className="referral-stat">
                        <div className="referral-stat-number">{referralStats.currentStreak}</div>
                        <div className="referral-stat-label">{t('profile.currentStreak')}</div>
                      </div>
                    </div>

                    {/* Share Your Referral Code Section */}
                    {referralCode && (
                      <div className="referral-share-section">
                        <h4 className="referral-section-title">{t('profile.shareYourCode')}</h4>
                        <div className="referral-code-display">
                          <div className="referral-code-box">
                            <code className="referral-code">{referralCode}</code>
                            <button
                              className="copy-btn"
                              onClick={copyReferralCode}
                              title={t('profile.copyToClipboard')}
                            >
                              <i className="bi bi-clipboard"></i>
                            </button>
                          </div>
                          <p className="referral-description">
                            {t('profile.shareCodeDescription')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Enter Referral Code Section */}
                    {showReferralInput && (
                      <div className="referral-input-section">
                        <h4 className="referral-section-title">{t('profile.enterReferralCode')}</h4>
                        <p className="referral-input-description">
                          {t('profile.enterReferralCodeDesc')}
                        </p>
                        <div className="referral-input-group">
                          <input
                            type="text"
                            className="referral-input"
                            placeholder={t('profile.enterReferralCodePlaceholder')}
                            value={inputReferralCode}
                            onChange={(e) => setInputReferralCode(e.target.value.toUpperCase())}
                            maxLength={8}
                            disabled={loading}
                          />
                          <button
                            className="apply-btn"
                            onClick={handleReferralSubmit}
                            disabled={loading || !inputReferralCode.trim()}
                          >
                            {loading ? t('profile.applying') : t('profile.apply')}
                          </button>
                        </div>
                      </div>
                    )}

                    {referralMessage && (
                      <div className={`referral-message ${referralMessage.includes('success') || referralMessage.includes('copied') ? 'success' : 'error'}`}>
                        <i className={`bi ${referralMessage.includes('success') || referralMessage.includes('copied') ? 'bi-check-circle' : 'bi-exclamation-circle'}`}></i>
                        {referralMessage}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Project Creation Panel - After Referral */}
              {isOwnProfile && (
                <div className="profile-panel-center">
                  <div className="project-creation-section">
                    <ProjectCreationEligibilityCard
                      className="mb-4"
                      initialEligibility={projectEligibility ?? undefined}
                      initialSettings={projectSettings ?? undefined}
                    />
                  </div>

                  {/* LinkedIn Authentication Management */}
                  <div className="mb-4">
                    <LinkedInAuthManager
                      showTitle={true}
                      compact={false}
                      initialAuthStatus={linkedInAuthStatus ?? undefined}
                    />
                  </div>
                </div>
              )}

              {/* Public Profile Panel - For viewing other users */}
              {!isOwnProfile && (
                <div className="profile-panel-public">
                  <div className="public-profile-info">
                    <h3 className="section-title">
                      <i className="bi bi-person-badge"></i>
                      {t('profile.publicProfile')}
                    </h3>
                    <div className="public-stats-grid">
                      <div className="public-stat">
                        <div className="public-stat-icon">
                          <i className="bi bi-trophy"></i>
                        </div>
                        <div className="public-stat-content">
                          <div className="public-stat-number">{(profile.points || 0).toLocaleString()}</div>
                          <div className="public-stat-label">{t('profile.totalPoints')}</div>
                        </div>
                      </div>

                      <div className="public-stat">
                        <div className="public-stat-icon">
                          <i className="bi bi-check-circle"></i>
                        </div>
                        <div className="public-stat-content">
                          <div className="public-stat-number">{(profile.completed_tasks || 0).toLocaleString()}</div>
                          <div className="public-stat-label">{t('profile.tasksCompleted')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </BasePageLayout>
  );
};

export default ProfilePage;
